import { kCreateOrganization } from '@/constants/constants-api-paths'
import { kPaxtonAppApiRESTBaseUrl } from '@/constants/constants-links'
import { getAuth } from 'firebase/auth'
import { nanoid } from 'nanoid'
import * as Sentry from '@sentry/browser'
import { CreateOrganizationResponseSchema } from '../schemas'
import { ApiError } from '@/util/errors/api-error'
import { CreateSelfServeOrganizationRequest, CreateOrganizationResponse } from '../schemas'
import { formatUnknownError, getResponseDebugContext, getTopLevelKeys, readResponseJson } from '@/util/api/response-body'
import { ApiResponsePayloadError } from '@/util/errors/api-response-payload-error'
import { BlockedWorkEmailApiError, getWorkEmailPolicyErrorResponse } from '@/organizations/work-email-policy-error'

function getErrorDetail(data: unknown): unknown {
  return data && typeof data === 'object' && !Array.isArray(data) ? (data as { detail?: unknown }).detail : undefined
}

/**
 * Post Create Organization
 *
 * Calls the backend endpoint to create a new organization for the current user.
 *
 * @param createOrganizationRequest - Data for the new organization
 * @returns CreateOrganizationResponse
 */
export async function postCreateOrganization(createOrganizationRequest: CreateSelfServeOrganizationRequest): Promise<CreateOrganizationResponse> {
  const sentryTransactionId = nanoid()

  try {
    const user = getAuth().currentUser
    if (!user) throw new Error('User not authenticated.')
    const token = await user.getIdToken()
    if (!token) throw new Error("Could not retrieve user's authentication token.")

    const requestOptions: RequestInit = {
      headers: {
        'Content-Type': 'application/json; charset=UTF-8',
        'X-Transaction-Id': sentryTransactionId,
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(createOrganizationRequest),
      method: 'POST',
    }

    const apiUrl = `${kPaxtonAppApiRESTBaseUrl()}${kCreateOrganization}`
    const response = await fetch(apiUrl, requestOptions)
    const { data, parseError } = await readResponseJson(response)

    if (!response.ok) {
      const errorData = parseError ? undefined : data
      const detail = getErrorDetail(errorData)
      const workEmailPolicyError = response.status === 400 ? getWorkEmailPolicyErrorResponse(errorData) : undefined
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error(`Fetch error - status: ${response.status}`), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseData: errorData,
            parseError: parseError ? formatUnknownError(parseError) : null,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })

      if (workEmailPolicyError) {
        throw new BlockedWorkEmailApiError(workEmailPolicyError.detail)
      }
      throw new ApiError(response.status, `Fetch error - status: ${response.status}`, detail)
    }

    if (parseError) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Create organization response JSON parse failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            parseError: formatUnknownError(parseError),
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid create organization response JSON')
    }

    const parsed = CreateOrganizationResponseSchema.safeParse(data)
    if (!parsed.success) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(new Error('Create organization response schema validation failed'), {
          extra: {
            ...getResponseDebugContext(response, apiUrl),
            responseKeys: getTopLevelKeys(data),
            issues: parsed.error.issues,
            onLine: navigator.onLine,
            cookieEnabled: navigator.cookieEnabled,
          },
        })
      })
      throw new ApiResponsePayloadError(response.status, 'Invalid create organization response shape', parsed.error.issues)
    }

    return parsed.data
  } catch (error) {
    // Skip api errors already captured above
    if (!(error instanceof ApiError)) {
      Sentry.withScope((scope) => {
        scope.setTags({ transaction_id: sentryTransactionId })
        Sentry.captureException(error, {
          extra: { onLine: navigator.onLine, cookieEnabled: navigator.cookieEnabled, organizationData: JSON.stringify(createOrganizationRequest) },
        })
      })
    }
    throw error
  }
}
